/**
 * Resource Viewer
 * Opens PDP resource PDFs (install guides, spec sheets) in an on-page dialog
 * instead of a new tab. Phones and browsers without a built-in PDF viewer
 * keep the link's default new-tab behavior, since an iframed PDF there shows
 * one page or nothing.
 */

if (typeof window.ResourceViewer === 'undefined') {
window.ResourceViewer = class ResourceViewer {
  constructor() {
    this.dialog = null;
    this.current = null;
    document.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('auxclick', (event) => {
      if (event.button !== 1) return;
      const link = event.target.closest('a[data-resource-viewer-link]');
      if (link) this.track('resource_open_tab', link.href, this.titleOf(link), { source: 'row' });
    });
  }

  canPreview() {
    return navigator.pdfViewerEnabled === true && window.matchMedia('(pointer: fine)').matches;
  }

  onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;

    const link = event.target.closest('a[data-resource-viewer-link]');
    if (!link) return;

    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (modified || !this.canPreview() || !this.getDialog()) {
      this.track('resource_open_tab', link.href, this.titleOf(link), { source: 'row' });
      return;
    }

    event.preventDefault();
    this.open(link.href, this.titleOf(link));
  }

  titleOf(link) {
    return link.querySelector('.pdp-resources-list__title')?.textContent.trim() || '';
  }

  getDialog() {
    if (this.dialog && this.dialog.isConnected) return this.dialog;

    const dialog = document.querySelector('[data-resource-viewer]');
    if (!dialog || typeof dialog.showModal !== 'function') return null;

    // The markup renders inside a <details> that may be closed, and inside a
    // container component-resources-collapsible.js can rewrite on variant change.
    document.body.appendChild(dialog);

    dialog.querySelector('[data-resource-viewer-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog.querySelector('[data-resource-viewer-frame]').removeAttribute('src');
      document.documentElement.classList.remove('resource-viewer-open');
      if (!this.current) return;
      const { url, title, openedAt } = this.current;
      this.current = null;
      this.track('resource_close', url, title, { seconds_open: Math.round((Date.now() - openedAt) / 1000) });
    });
    const openLink = dialog.querySelector('[data-resource-viewer-open]');
    const trackOpenTab = () => this.track('resource_open_tab', openLink.href, this.current?.title || '', { source: 'dialog' });
    openLink.addEventListener('click', trackOpenTab);
    openLink.addEventListener('auxclick', (event) => {
      if (event.button === 1) trackOpenTab();
    });
    dialog.querySelector('[data-resource-viewer-download]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.download(event.currentTarget);
      this.track('resource_download', event.currentTarget.href, this.current?.title || '');
    });

    this.dialog = dialog;
    return dialog;
  }

  open(url, title) {
    const dialog = this.dialog;
    const frame = dialog.querySelector('[data-resource-viewer-frame]');
    const download = dialog.querySelector('[data-resource-viewer-download]');

    dialog.querySelector('[data-resource-viewer-title]').textContent = title;
    dialog.querySelector('[data-resource-viewer-open]').href = url;
    if (download) {
      download.href = url;
      download.setAttribute('download', this.fileName(url));
    }
    frame.title = title;
    frame.src = url;

    document.documentElement.classList.add('resource-viewer-open');
    dialog.showModal();
    this.current = { url, title, openedAt: Date.now() };
    this.track('resource_open_dialog', url, title);
  }

  /**
   * GA4 is out of custom dimensions, so the action is in the event name. PostHog
   * picks these up from dataLayer by their resource_ prefix.
   */
  track(name, url, title, extra = {}) {
    const product = window.GA4PageContext?.product;
    const variantId = document.querySelector('.main-product-form [name="id"]')?.value || product?.variantId;
    const params = {
      file_name: this.fileName(url),
      resource_title: title,
      ...(product?.id ? { product_id: String(product.id) } : {}),
      ...(variantId ? { variant_id: String(variantId) } : {}),
      ...extra,
    };
    if (window.GA4CustomEvents?.pushCustomEvent) {
      window.GA4CustomEvents.pushCustomEvent(name, params);
    } else if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  }

  fileName(url) {
    try {
      return decodeURIComponent(new URL(url).pathname.split('/').pop()) || 'document.pdf';
    } catch {
      return 'document.pdf';
    }
  }

  /**
   * The `download` attribute is ignored for cross-origin URLs, so fetch the
   * file and save it from a blob URL. Without CORS on the asset host the
   * fetch fails and the file opens in a new tab instead.
   */
  async download(link) {
    try {
      const response = await fetch(link.href, { mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blobUrl = URL.createObjectURL(await response.blob());
      const temp = document.createElement('a');
      temp.href = blobUrl;
      temp.download = link.getAttribute('download');
      document.body.appendChild(temp);
      temp.click();
      temp.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch {
      window.open(link.href, '_blank', 'noopener');
    }
  }
};

window.resourceViewer = new window.ResourceViewer();
}
